// src/components/Widgets/StaffWidget.jsx
// Upgrades: overflow-safe rows, responsive wrap for controls (no UI change)
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import WidgetHeader from "../Common/WidgetHeader";
import StaffModal from "../Modals/StaffModal";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useStaff } from "../../contexts/StaffContext";
import { navigateTo, ROUTES } from "../../lib/nav";
import { useTheme } from "../../core/theme/ThemeProvider";

const LS_TASKS = "mudoTasks";

function normalizeName(s) {
  if (!s) return "";
  const parts = String(s).trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]} ${parts[1]}`;
  return parts[0] || "";
}
function displayName(staff) {
  if (staff?.firstName || staff?.lastName) {
    return `${staff.firstName || ""} ${staff.lastName || ""}`.trim();
  }
  if (staff?.name) return normalizeName(staff.name);
  return "Unnamed";
}
function initialsFromName(name) {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("");
}

// Accepts either (id, patch) or ({ id, ...patch })
function callUpdate(updateFn, id, patch) {
  if (typeof updateFn !== "function") return;
  if (updateFn.length >= 2) updateFn(id, patch);
  else updateFn({ id, ...patch });
}

export default function StaffWidget({ backTo = "/dashboard" }) {
  const navigate = useNavigate();
  const staffCtx = (useStaff && useStaff()) || {};
  const { staffList = [], addStaff, updateStaff } = staffCtx;
  const { mode: themeMode } = useTheme();

  const fb = (useFeedback && useFeedback()) || {};
  const play = fb.play || (() => {});
  const buzz = fb.buzz || (() => {});

  const [mode, setMode] = useState("most-used"); // "most-used" | "recent"
  const [filter, setFilter] = useState("All");   // "All" | "Active" | "Inactive"
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  // Load task usage from localStorage once
  const usageById = useMemo(() => {
    let tasks = [];
    try {
      tasks = JSON.parse(localStorage.getItem(LS_TASKS)) || [];
    } catch {
      tasks = [];
    }
    const counts = new Map();
    const bump = (id) => id && counts.set(id, (counts.get(id) || 0) + 1);
    for (const t of tasks) {
      if (t?.assigneeId) bump(t.assigneeId);
      if (t?.staffId) bump(t.staffId);
      if (Array.isArray(t?.assignedStaffIds)) t.assignedStaffIds.forEach(bump);
    }
    return counts;
  }, []);

  // Score lists
  const mostUsed = useMemo(() => {
    const scored = staffList.map((s) => {
      const id = s.id || s._id || s.uuid;
      const usage = usageById.get(id) || 0;
      const fallback = Number(s.hoursPerWeek) || 0;
      return { staff: s, usage, fallback, name: displayName(s) };
    });
    scored.sort((a, b) => {
      if (b.usage !== a.usage) return b.usage - a.usage;
      if (b.fallback !== a.fallback) return b.fallback - a.fallback;
      return a.name.localeCompare(b.name);
    });
    return scored.map((x) => x.staff);
  }, [staffList, usageById]);

  const recentlyAdded = useMemo(() => {
    const scored = staffList.map((s) => {
      const created =
        Number(s.createdAt) ||
        Number(String(s.id || s._id || s.uuid || "").replace(/\D/g, "").slice(-13)) ||
        0;
      return { staff: s, created };
    });
    scored.sort((a, b) => b.created - a.created);
    return scored.map((x) => x.staff);
  }, [staffList]);

  // Apply mode + filter
  const baseRows = mode === "recent" ? recentlyAdded : mostUsed;
  const rows = useMemo(() => {
    return baseRows
      .filter((s) =>
        filter === "All"
          ? true
          : filter === "Active"
          ? s.isActive !== false
          : s.isActive === false
      )
      .slice(0, 3);
  }, [baseRows, filter]);

  // Totals
  const totalMembers = staffList.length;
  const activeMembers = staffList.filter((s) => s.isActive !== false).length;
  const totalWeeklyCostActive = staffList.reduce((sum, s) => {
    if (s.isActive === false) return sum;
    return sum + (Number(s.hourlyRate) || 0) * (Number(s.hoursPerWeek) || 0);
  }, 0);

  // Modal handlers
  const openAdd = () => {
    setEditingStaff(null);
    setModalOpen(true);
  };
  const openEdit = (staff) => {
    setEditingStaff(staff);
    setModalOpen(true);
  };
  const handleSave = (data) => {
    try {
      if (editingStaff) {
        callUpdate(updateStaff, editingStaff.id, data);
      } else {
        addStaff?.(data);
      }
      play("success");
      buzz("success");
    } catch {
      play("error");
      buzz("error");
    } finally {
      setModalOpen(false);
      setEditingStaff(null);
    }
  };

  const pill = (active) =>
    `px-2 py-1 text-xs rounded ${
      active
        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
        : "bg-transparent border border-black/10 dark:border-white/10"
    }`;

  const chip = (active) =>
    `px-2 py-1 text-[11px] sm:text-[11px] text-[10px] rounded-full ${
      active ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-500/15 text-zinc-300"
    } border border-white/10`;

  const primaryBtn =
    "px-2 py-1 text-xs rounded font-semibold transition bg-[var(--mudo-primary)] text-[var(--mudo-onPrimary,#0b0b0b)] hover:brightness-110";
  const secondaryBtn =
    themeMode === "dark"
      ? "px-2 py-1 text-xs rounded border border-white/20 bg-white/5 hover:bg-white/10"
      : "px-2 py-1 text-xs rounded border border-black/15 bg-black/5 hover:bg-black/10";

  return (
    <div className="rounded-lg p-4 h-full flex flex-col bg-transparent text-inherit">
      <WidgetHeader
        title={
          <span className="flex items-center gap-2">
            <span className="text-xl" aria-hidden></span> Staff
          </span>
        }
        to={backTo}
        right={
          <div className="flex items-center gap-2">
            {/* Mode toggles */}
            <div className="hidden sm:flex items-center gap-1">
              <button className={pill(mode === "most-used")} onClick={() => setMode("most-used")}>
                Most used
              </button>
              <button className={pill(mode === "recent")} onClick={() => setMode("recent")}>
                Recently added
              </button>
            </div>

            {/* Filter chips */}
            <div className="flex items-center gap-1">
              <button className={pill(filter === "All")} onClick={() => setFilter("All")}>All</button>
              <button className={pill(filter === "Active")} onClick={() => setFilter("Active")}>Active</button>
              <button className={pill(filter === "Inactive")} onClick={() => setFilter("Inactive")}>Inactive</button>
            </div>

            <button
              onClick={openAdd}
              className={primaryBtn}
            >
              + Add
            </button>
            {staffList.length > 0 && (
              <button
                onClick={() => navigateTo(navigate, ROUTES.STAFF, { source: 'StaffWidget:manage' })}
                className={secondaryBtn}
              >
                Manage
              </button>
            )}
          </div>
        }
      />

      {/* List */}
      <div className="space-y-2">
        {rows.length ? (
          rows.map((s) => {
            const name = displayName(s);
            const initials = initialsFromName(name);
            const id = s.id || s._id || s.uuid;
            const usage = usageById.get(id) || 0;
            const active = s.isActive !== false;

            const toggleActive = () => callUpdate(updateStaff, id, { isActive: !active });

            return (
              <li
                key={id || name}
                className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:gap-3 rounded border border-white/10 hover:border-white/20 transition p-2 overflow-hidden"
              >
                <button
                  onClick={() => openEdit(s)}
                  className="flex-1 min-w-0 max-w-full flex items-center gap-3 text-left"
                  title="Edit staff"
                >
                  {/* Avatar / Color ring */}
                  <div className="relative shrink-0">
                    {s.photoUrl ? (
                      <img
                        src={s.photoUrl}
                        alt={name}
                        className="h-9 w-9 rounded-full object-cover border border-white/10"
                        style={{ boxShadow: s.color ? `0 0 0 2px ${s.color}` : undefined }}
                      />
                    ) : (
                      <div
                        className="h-9 w-9 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center text-xs font-semibold border border-white/10"
                        style={{ boxShadow: s.color ? `0 0 0 2px ${s.color}` : undefined }}
                      >
                        {initials}
                      </div>
                    )}
                    {/* Status dot */}
                    <span
                      className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border border-black/40"
                      style={{ background: active ? "#10b981" : "#6b7280" }}
                      title={active ? "Active" : "Inactive"}
                    />
                  </div>

                  {/* Name + details */}
                  <div className="min-w-0 max-w-full">
                    <div className="font-medium text-sm truncate">{name}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      {(Number(s.hoursPerWeek) || 0)} h/week • ${(+s.hourlyRate || 0).toFixed(2)}/h
                      {s.email ? ` • ${s.email}` : ""}
                    </div>
                  </div>
                </button>

                {/* Right-side controls (wrap under on very small widths) */}
                <div className="flex w-full sm:w-auto items-center gap-2 sm:gap-2 shrink-0 sm:ml-auto justify-between sm:justify-end">
                  <span className={chip(active)}>{active ? "Active" : "Inactive"}</span>

                  {/* small color chip */}
                  <span
                    className="inline-block h-4 w-4 rounded-full border border-white/20"
                    style={{ background: s.color || "#ffffff" }}
                    title={s.color || "No color"}
                  />

                  <div className="text-[10px] sm:text-[11px] text-zinc-500">
                    {mode === "most-used" && (usage > 0 ? `${usage} task${usage > 1 ? "s" : ""}` : "—")}
                  </div>

                  <button
                    onClick={toggleActive}
                    className="text-[10px] sm:text-[11px] underline opacity-70 hover:opacity-100"
                    title={active ? "Set inactive" : "Set active"}
                  >
                    {active ? "deactivate" : "activate"}
                  </button>

                  <button
                    onClick={() => openEdit(s)}
                    className="text-[10px] sm:text-[11px] underline opacity-70 hover:opacity-100"
                  >
                    edit
                  </button>
                </div>
              </li>
            );
          })
        ) : (
          <div className="text-sm opacity-80">No staff match this view. Add team members or adjust filters.</div>
        )}
      </div>

      {/* Footer totals */}
      <div
        className="mt-3 pt-3 text-sm flex items-center justify-between"
        style={{ borderTop: "1px solid color-mix(in oklab, currentColor 12%, transparent)" }}
      >
        <div>
          <span className="opacity-70">Members:</span>{" "}
          <strong>{activeMembers}</strong>
          <span className="opacity-60"> / {totalMembers}</span>
        </div>
        <div className="text-right">
          <span className="opacity-70">Active weekly cost:</span>{" "}
          <strong>${totalWeeklyCostActive.toFixed(2)}</strong>
        </div>
      </div>

      {/* Inline Staff Modal */}
      {modalOpen && (
        <StaffModal
          initialStaff={editingStaff}
          onClose={() => {
            setModalOpen(false);
            setEditingStaff(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

StaffWidget.propTypes = {
  backTo: PropTypes.string,
};
